"use client";

import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useRouter } from "next/navigation";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import Link from "next/link";
import NextImage from '@/components/ui/MediaImage';
import clsx from "clsx";
import { inputFocusAnimation, inputHoverAnimation } from "@/lib/animations";
import type { PageUrlMap } from "@/types/blog";
import { resolveUrlFromMap, buildLocalizedLink } from '@/lib/blog-transformers';
import { UrlBuilder } from '@/lib/url-builder';

export interface BlogSearchResult {
  posts: Array<{
    id: number;
    type: "post";
    attributes: {
      title: string;
      slug: string;
      summary?: string | null;
    };
    relationships?: {
      cover_image?: {
        data?: {
          id: number;
          attributes: {
            url: string;
            alt?: string | null;
            width?: number | null;
            height?: number | null;
          };
        } | null;
      } | null;
      categories?: Array<{
        id?: number;
        attributes?: {
          slug?: string | null;
          name?: string | null;
        };
      }> | null;
    };
  }>;
  categories: Array<{
    id: number;
    type: "category";
    attributes: {
      name: string;
      slug: string;
      post_count?: number | null;
    };
  }>;
  tags: Array<{
    id: number;
    type: "tag";
    attributes: {
      name: string;
      slug: string;
      post_count?: number | null;
    };
  }>;
}

export interface BlogSearchDropdownProps {
  language: string;
  defaultLanguage: string;
  pageUrls?: PageUrlMap;
  translations?: Record<string, string>;
}

const MIN_SEARCH_LENGTH = 3;
const DEBOUNCE_DELAY = 50;
const CACHE_TTL = 5 * 60 * 1000;
const MAX_CACHE_SIZE = 50;

const translate = (
  translations: Record<string, string> | undefined,
  key: string,
  fallback: string
): string => translations?.[key] ?? fallback;

const highlightText = (text: string, searchTerm: string): React.ReactNode => {
  if (!searchTerm || !text) {
    return text;
  }

  const escapedTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escapedTerm})`, 'gi');
  const parts = text.split(regex);

  return parts.map((part, index) => {
    if (part.toLowerCase() === searchTerm.toLowerCase()) {
      return (
        <mark
          key={index}
          className="bg-alsero-first/25 text-alsero-first font-bold px-1 py-0.5 rounded-sm"
        >
          {part}
        </mark>
      );
    }
    return <span key={index}>{part}</span>;
  });
};

const useSafeRouter = () => {
  try {
    return useRouter();
  } catch {
    return null;
  }
};

export default function BlogSearchDropdown({
  language,
  defaultLanguage,
  pageUrls,
  translations,
}: BlogSearchDropdownProps) {
  const router = useSafeRouter();
  const prefersReduced = useReducedMotion();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState<BlogSearchResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [error, setError] = useState<string | null>(null);
  const [searchTime, setSearchTime] = useState<number | null>(null);
  const [totalResults, setTotalResults] = useState<number>(0);

  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  
  const cacheRef = useRef<Map<string, { data: BlogSearchResult; timestamp: number; meta: any }>>(new Map());
  
  const pendingRequestsRef = useRef<Map<string, Promise<BlogSearchResult>>>(new Map());

  // Blog post URL'leri için 'blog' base URL'ini kullan (blog-detail yerine)
  // Çünkü blog postları /blog/{post-slug} formatında olmalı
  const detailBase = useMemo(
    () => resolveUrlFromMap(pageUrls, "blog", language, defaultLanguage, "/blog"),
    [pageUrls, language, defaultLanguage]
  );
  // Kategoriler için /blog/{category-slug} formatı
  const categoryBase = useMemo(
    () => resolveUrlFromMap(pageUrls, "blog", language, defaultLanguage, "/blog"),
    [pageUrls, language, defaultLanguage]
  );
  const urlBuilder = useMemo(
    () => pageUrls ? new UrlBuilder(pageUrls, language, defaultLanguage) : null,
    [pageUrls, language, defaultLanguage]
  );

  const flattenedResults = useMemo(() => {
    const items: Array<{
      type: "post" | "category" | "tag";
      id: number;
      title: string;
      href: string;
      summary?: string | null;
      image?: string | null;
      count?: number | null;
    }> = [];

    if (results) {
      results.categories.forEach((cat) => {
        items.push({
          type: "category",
          id: cat.id,
          title: cat.attributes.name,
          href: buildLocalizedLink(categoryBase, cat.attributes.slug, defaultLanguage),
          count: cat.attributes.post_count ?? null,
        });
      });

      results.posts.forEach((post) => {
        const imageUrl = post.relationships?.cover_image?.data?.attributes?.url || null;
        
        // Yeni URL yapısı: /blog/{post-slug} (kategori slug'ı URL'de yok, sadece breadcrumb'da)
        const postSlug = post.attributes.slug;
        
        items.push({
          type: "post",
          id: post.id,
          title: post.attributes.title,
          href: buildLocalizedLink(detailBase, postSlug, defaultLanguage),
          summary: post.attributes.summary,
          image: imageUrl,
        });
      });

      results.tags.forEach((tag) => {
        items.push({
          type: "tag",
          id: tag.id,
          title: tag.attributes.name,
          href: urlBuilder?.buildBlogTagUrl(tag.attributes.slug) ?? '#',
          count: tag.attributes.post_count ?? null,
        });
      });
    }

    return items;
  }, [results, categoryBase, detailBase, urlBuilder, defaultLanguage]);

  const performSearch = useCallback(
    async (term: string) => {
      const cacheKey = `${language}:${term.toLowerCase().trim()}`;
      
      const cached = cacheRef.current.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
        const searchResults = cached.data;
        const processingTime = cached.meta?.processingTimeMs ?? 0;
        const total = (searchResults.posts?.length || 0) + 
                      (searchResults.categories?.length || 0) + 
                      (searchResults.tags?.length || 0);
        
        setResults(searchResults);
        setSearchTime(processingTime);
        setTotalResults(total);
        setSelectedIndex(-1);
        setIsLoading(false);
        return;
      }

      const pendingRequest = pendingRequestsRef.current.get(cacheKey);
      if (pendingRequest) {
        try {
          const searchResults = await pendingRequest;
          const total = (searchResults.posts?.length || 0) + 
                        (searchResults.categories?.length || 0) + 
                        (searchResults.tags?.length || 0);
          
          setResults(searchResults);
          setSearchTime(0);
          setTotalResults(total);
          setSelectedIndex(-1);
          setIsLoading(false);
          return;
        } catch (err) {
        }
      }

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();

      setIsLoading(true);
      setError(null);
      setSearchTime(null);
      setTotalResults(0);

      const startTime = performance.now();
      const searchPromise = (async () => {
        try {
          const controller = abortControllerRef.current;
          if (!controller) {
            throw new Error('AbortController not initialized');
          }

          const response = await fetch(
            `/api/blog/search?q=${encodeURIComponent(term)}&language=${encodeURIComponent(language)}`,
            {
              signal: controller.signal,
            }
          );

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || errorData.message || "Arama yapılamadı");
          }

          const data = await response.json();
          const searchResults = data.data || { posts: [], categories: [], tags: [] };
          
          const backendProcessingTimeMs = data.meta?.processingTimeMs;
          const processingTime = backendProcessingTimeMs ?? Math.round(performance.now() - startTime);
          
          if (cacheRef.current.size >= MAX_CACHE_SIZE) {
            let oldestKey: string | null = null;
            let oldestTime = Date.now();
            for (const [key, value] of cacheRef.current.entries()) {
              if (value.timestamp < oldestTime) {
                oldestTime = value.timestamp;
                oldestKey = key;
              }
            }
            if (oldestKey) {
              cacheRef.current.delete(oldestKey);
            }
          }
          
          cacheRef.current.set(cacheKey, {
            data: searchResults,
            timestamp: Date.now(),
            meta: { processingTimeMs: processingTime, ...data.meta }
          });
          
          const now = Date.now();
          for (const [key, value] of cacheRef.current.entries()) {
            if (now - value.timestamp >= CACHE_TTL) {
              cacheRef.current.delete(key);
            }
          }
          
          pendingRequestsRef.current.delete(cacheKey);
          
          return searchResults;
        } catch (err: unknown) {
          pendingRequestsRef.current.delete(cacheKey);
          throw err;
        }
      })();

      pendingRequestsRef.current.set(cacheKey, searchPromise);

      try {
        const searchResults = await searchPromise;
        
        const total = (searchResults.posts?.length || 0) + 
                      (searchResults.categories?.length || 0) + 
                      (searchResults.tags?.length || 0);
        
        const cachedMeta = cacheRef.current.get(cacheKey)?.meta;
        const processingTime = cachedMeta?.processingTimeMs ?? Math.round(performance.now() - startTime);
        
        setResults(searchResults);
        setSearchTime(processingTime);
        setTotalResults(total);
        setSelectedIndex(-1);
      } catch (err: unknown) {
        if (err instanceof Error && err.name === "AbortError") {
          return;
        }
        setError(err instanceof Error ? err.message : "Arama yapılırken bir hata oluştu");
        setResults(null);
        setSearchTime(null);
        setTotalResults(0);
      } finally {
        setIsLoading(false);
      }
    },
    [language]
  );

  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    const trimmedTerm = searchTerm.trim();

    if (trimmedTerm.length < MIN_SEARCH_LENGTH) {
      setResults(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    debounceTimerRef.current = setTimeout(() => {
      performSearch(trimmedTerm);
    }, DEBOUNCE_DELAY);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchTerm, performSearch]);

  const handleInputFocus = () => {
    if (searchTerm.trim().length >= MIN_SEARCH_LENGTH && results) {
      setIsOpen(true);
      setTimeout(() => {
        inputRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start',
          inline: 'nearest'
        });
      }, 100);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (value.trim().length >= MIN_SEARCH_LENGTH) {
      setIsOpen(true);
      setTimeout(() => {
        inputRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start',
          inline: 'nearest'
        });
      }, 100);
    } else {
      setIsOpen(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || flattenedResults.length === 0) {
      if (e.key === "Enter" && searchTerm.trim().length >= MIN_SEARCH_LENGTH) {
        if (flattenedResults.length > 0) {
          if (router) {
            router.push(flattenedResults[0].href);
          } else if (typeof window !== 'undefined') {
            window.location.href = flattenedResults[0].href;
          }
          setIsOpen(false);
          setSearchTerm("");
        }
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < flattenedResults.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < flattenedResults.length) {
          const targetHref = flattenedResults[selectedIndex].href;
          if (router) {
            router.push(targetHref);
          } else if (typeof window !== 'undefined') {
            window.location.href = targetHref;
          }
          setIsOpen(false);
          setSearchTerm("");
        }
        break;
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        inputRef.current?.blur();
        break;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        setIsOpen(false);
        inputRef.current?.blur();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen]);

  useEffect(() => {
    return () => {
      cacheRef.current.clear();
      pendingRequestsRef.current.clear();
    };
  }, []);

  const hasResults =
    results &&
    (results.posts.length > 0 ||
      results.categories.length > 0 ||
      results.tags.length > 0);

  const showDropdown = isOpen && (isLoading || hasResults || error || searchTerm.trim().length >= MIN_SEARCH_LENGTH);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      const scrollToInput = () => {
        const inputElement = inputRef.current;
        if (inputElement) {
          const rect = inputElement.getBoundingClientRect();
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          const targetY = scrollTop + rect.top;
          
          window.scrollTo({
            top: Math.max(0, targetY),
            behavior: 'smooth'
          });
        }
      };
      
      const timeoutId = setTimeout(scrollToInput, 150);
      
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen]);

  return (
    <>
      {/* Search Input Container - Sticky, scroll ile üstte sabit kalır */}
      <div className="sticky top-0 z-30 bg-white" ref={dropdownRef}>
        {/* Backdrop - scroll'u engellemez, sadece görsel ve tıklanabilir */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="fixed inset-0 bg-black/20 bg-clip-padding backdrop-filter backdrop-blur-xl bg-opacity-20 z-20"
              onClick={(e) => {
                // Sadece backdrop'a tıklanırsa kapat (dropdown içine tıklanırsa kapatma)
                if (e.target === e.currentTarget) {
                  setIsOpen(false);
                  inputRef.current?.blur();
                }
              }}
            />
          )}
        </AnimatePresence>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (flattenedResults.length > 0) {
              const targetHref = flattenedResults[0].href;
              if (router) {
                router.push(targetHref);
              } else if (typeof window !== 'undefined') {
                window.location.href = targetHref;
              }
              setIsOpen(false);
              setSearchTerm("");
            }
          }}
        >
          <div className="bg-white flex flex-row justify-between items-center p-4 relative z-30">
            <motion.input
              ref={inputRef}
              type="text"
              placeholder={translate(
                translations,
                "blog_search_placeholder",
                language === "tr" ? "Ara..." : "Search..."
              )}
              className="text-alsero-light-gray text-lg w-full outline-none"
              value={searchTerm}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              onKeyDown={handleKeyDown}
              whileFocus={inputFocusAnimation}
              whileHover={inputHoverAnimation}
            />
            <motion.button
              type="submit"
              className="cursor-pointer border border-alsero-soft-white hover:border-alsero-dark-gray transform-border ease-in-out duration-300 p-3 shrink-0"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              aria-label={translate(
                translations,
                "blog_search_button_aria_label",
                language === "tr" ? "Ara" : "Search"
              )}
            >
              {isLoading ? (
                <div className="w-4 h-4 border-2 border-alsero-first border-t-transparent rounded-full animate-spin" />
              ) : (
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M15.6219 14.5208L11.8571 10.7906C12.8251 9.69436 13.3555 8.31508 13.3555 6.87919C13.3555 3.52438 10.4903 0.794922 6.96875 0.794922C3.44717 0.794922 0.582031 3.52438 0.582031 6.87919C0.582031 10.234 3.44717 12.9635 6.96875 12.9635C8.2908 12.9635 9.55064 12.5836 10.6278 11.8625L14.4212 15.621C14.5798 15.7778 14.793 15.8643 15.0216 15.8643C15.2379 15.8643 15.4431 15.7858 15.5989 15.6429C15.9299 15.3395 15.9404 14.8364 15.6219 14.5208ZM6.96875 2.38212C9.57175 2.38212 11.6894 4.39945 11.6894 6.87919C11.6894 9.35893 9.57175 11.3763 6.96875 11.3763C4.36574 11.3763 2.24813 9.35893 2.24813 6.87919C2.24813 4.39945 4.36574 2.38212 6.96875 2.38212Z"
                    fill="black"
                  />
                </svg>
              )}
            </motion.button>
          </div>
        </form>

        {/* Dropdown Results */}
        <AnimatePresence>
          {showDropdown && (
            <motion.div
              initial={
                prefersReduced
                  ? { opacity: 0 }
                  : { opacity: 0, y: -6, scaleY: 0.98, transformOrigin: "top center" }
              }
              animate={
                prefersReduced
                  ? { opacity: 1 }
                  : { opacity: 1, y: 0, scaleY: 1 }
              }
              exit={
                prefersReduced
                  ? { opacity: 0 }
                  : { opacity: 0, y: -4, scaleY: 0.985 }
              }
              transition={{
                duration: prefersReduced ? 0.001 : 0.18,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-2xl max-h-[70vh] overflow-y-auto z-30 will-change-transform will-change-opacity overscroll-contain"
              style={{ 
                transform: "translateZ(0)",
                maxHeight: "min(400px, 70vh)", // Responsive max height
              }}
            >
              {/* Loading State */}
              {isLoading && (
                <div className="p-8 text-center">
                  <div className="w-8 h-8 border-3 border-alsero-first border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                  <p className="text-alsero-light-gray text-sm">
                    {translate(
                      translations,
                      "blog_search_loading",
                      language === "tr" ? "Aranıyor..." : "Searching..."
                    )}
                  </p>
                </div>
              )}

              {/* Error State */}
              {error && !isLoading && (
                <div className="p-8 text-center">
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}

              {/* Minimum Character Warning */}
              {!isLoading &&
                !error &&
                searchTerm.trim().length > 0 &&
                searchTerm.trim().length < MIN_SEARCH_LENGTH && (
                  <div className="p-8 text-center">
                    <p className="text-alsero-light-gray text-sm">
                      {translate(
                        translations,
                        "blog_search_min_chars",
                        language === "tr"
                          ? "En az 3 karakter giriniz"
                          : "Enter at least 3 characters"
                      )}
                    </p>
                  </div>
                )}

              {/* No Results */}
              {!isLoading &&
                !error &&
                searchTerm.trim().length >= MIN_SEARCH_LENGTH &&
                !hasResults && (
                  <div className="p-8 text-center">
                    <p className="text-alsero-light-gray text-sm">
                      {translate(
                        translations,
                        "blog_search_no_results",
                        language === "tr"
                          ? "Sonuç bulunamadı"
                          : "No results found"
                      )}
                    </p>
                  </div>
                )}

              {/* Results */}
              {!isLoading && !error && hasResults && (
                <div className="py-2">
                  {/* Categories */}
                  {results.categories.length > 0 && (
                    <div className="px-4 py-2">
                      <h3 className="text-xs font-semibold text-alsero-light-gray uppercase mb-2">
                        {translate(
                          translations,
                          "blog_search_categories",
                          language === "tr" ? "Kategoriler" : "Categories"
                        )}
                      </h3>
                      {results.categories.map((cat, idx) => {
                        const globalIndex = idx;
                        const href = buildLocalizedLink(
                          categoryBase,
                          cat.attributes.slug,
                          defaultLanguage
                        );
                        const isSelected = selectedIndex === globalIndex;

                        return (
                          <motion.div
                            key={`cat-${cat.id}`}
                            initial={
                              prefersReduced
                                ? { opacity: 0 }
                                : { opacity: 0, y: -6 }
                            }
                            animate={
                              prefersReduced
                                ? { opacity: 1 }
                                : { opacity: 1, y: 0 }
                            }
                            transition={{
                              duration: prefersReduced ? 0.001 : 0.12,
                              ease: [0.22, 1, 0.36, 1],
                              delay: idx * 0.02,
                            }}
                          >
                            <Link
                              href={href}
                              className={clsx(
                                "block px-4 py-3 rounded-lg transition-colors duration-200",
                                isSelected
                                  ? "bg-alsero-first/10 text-alsero-first border-l-4 border-alsero-first"
                                  : "text-alsero-dark-gray hover:bg-gray-50 hover:text-alsero-first"
                              )}
                              onClick={() => {
                                setIsOpen(false);
                                setSearchTerm("");
                              }}
                            >
                              <div className="flex items-center justify-between">
                                <span className="font-medium">
                                  {highlightText(cat.attributes.name, searchTerm.trim())}
                                </span>
                                {cat.attributes.post_count !== null &&
                                  cat.attributes.post_count !== undefined && (
                                    <span className="text-xs text-alsero-light-gray">
                                      {cat.attributes.post_count}
                                    </span>
                                  )}
                              </div>
                            </Link>
                          </motion.div>
                        );
                      })}
                    </div>
                  )}

                  {/* Posts */}
                  {results.posts.length > 0 && (
                    <div className="px-4 py-2 border-t border-gray-100">
                      <h3 className="text-xs font-semibold text-alsero-light-gray uppercase mb-2">
                        {translate(
                          translations,
                          "blog_search_posts",
                          language === "tr" ? "Yazılar" : "Posts"
                        )}
                      </h3>
                      {results.posts.map((post, idx) => {
                        const globalIndex =
                          results.categories.length + idx;
                        
                        // Category slug'ını al (post'un category'si varsa)
                        const postSlug = post.attributes.slug;
                        const categorySlug = post.relationships?.categories?.[0]?.attributes?.slug ||
                                             null;
                        
                        // Yeni URL yapısı: /blog/{category-slug}/{post-slug}
                        // Category yoksa: /blog/{post-slug}
                        // Yeni URL yapısı: /blog/{post-slug} (kategori slug'ı URL'de yok)
                        const fullSlug = postSlug;
                        
                        const href = buildLocalizedLink(
                          detailBase,
                          fullSlug,
                          defaultLanguage
                        );
                        const isSelected = selectedIndex === globalIndex;
                        const rawImageUrl = post.relationships?.cover_image?.data?.attributes?.url;
                        const imageUrl = rawImageUrl || "https://placehold.co/400x300?text=Blog";
                        const imageAlt = post.relationships?.cover_image?.data?.attributes?.alt ?? post.attributes.title;

                        return (
                          <motion.div
                            key={`post-${post.id}`}
                            initial={
                              prefersReduced
                                ? { opacity: 0 }
                                : { opacity: 0, y: -6 }
                            }
                            animate={
                              prefersReduced
                                ? { opacity: 1 }
                                : { opacity: 1, y: 0 }
                            }
                            transition={{
                              duration: prefersReduced ? 0.001 : 0.12,
                              ease: [0.22, 1, 0.36, 1],
                              delay: idx * 0.02,
                            }}
                          >
                            <Link
                              href={href}
                              className={clsx(
                                "block px-4 py-3 rounded-lg transition-colors duration-200",
                                isSelected
                                  ? "bg-alsero-first/10 text-alsero-first border-l-4 border-alsero-first"
                                  : "text-alsero-dark-gray hover:bg-gray-50 hover:text-alsero-first"
                              )}
                              onClick={() => {
                                setIsOpen(false);
                                setSearchTerm("");
                              }}
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-16 h-16 shrink-0 rounded overflow-hidden">
                                  <NextImage
                                    src={imageUrl}
                                    alt={imageAlt}
                                    width={64}
                                    height={64}
                                    className="w-full h-full object-cover"
                                    unoptimized={imageUrl.startsWith('/api/media/proxy/')}
                                  />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h4 className="font-medium text-sm line-clamp-1">
                                    {highlightText(post.attributes.title, searchTerm.trim())}
                                  </h4>
                                  {post.attributes.summary && (
                                    <p className="text-xs text-alsero-light-gray line-clamp-1 mt-1">
                                      {highlightText(post.attributes.summary, searchTerm.trim())}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </Link>
                          </motion.div>
                        );
                      })}
                    </div>
                  )}

                  {/* Tags */}
                  {results.tags.length > 0 && (
                    <div className="px-4 py-2 border-t border-gray-100">
                      <h3 className="text-xs font-semibold text-alsero-light-gray uppercase mb-2">
                        {translate(
                          translations,
                          "blog_search_tags",
                          language === "tr" ? "Etiketler" : "Tags"
                        )}
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {results.tags.map((tag, idx) => {
                          const globalIndex =
                            results.categories.length +
                            results.posts.length +
                            idx;
                          const href =                           urlBuilder?.buildBlogTagUrl(tag.attributes.slug) ?? '#';
                          const isSelected = selectedIndex === globalIndex;

                          return (
                            <motion.div
                              key={`tag-${tag.id}`}
                              initial={
                                prefersReduced
                                  ? { opacity: 0 }
                                  : { opacity: 0, scale: 0.9 }
                              }
                              animate={
                                prefersReduced
                                  ? { opacity: 1 }
                                  : { opacity: 1, scale: 1 }
                              }
                              transition={{
                                duration: prefersReduced ? 0.001 : 0.12,
                                ease: [0.22, 1, 0.36, 1],
                                delay: idx * 0.02,
                              }}
                            >
                              <Link
                                href={href}
                                className={clsx(
                                  "inline-block px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200",
                                  isSelected
                                    ? "bg-alsero-first text-white"
                                    : "bg-gray-100 text-alsero-dark-gray hover:bg-alsero-first hover:text-white"
                                )}
                                onClick={() => {
                                  setIsOpen(false);
                                  setSearchTerm("");
                                }}
                              >
                                {highlightText(tag.attributes.name, searchTerm.trim())}
                              </Link>
                            </motion.div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Search Stats Footer */}
                  {searchTime !== null && totalResults > 0 && (
                    <div className="px-4 py-3 border-t border-gray-100 bg-gray-50/50">
                      <div className="flex items-center justify-between text-xs text-alsero-light-gray">
                        <span>
                          {totalResults}{" "}
                          {translate(
                            translations,
                            "blog_search_results_found",
                            language === "tr" ? "sonuç bulundu" : "results found"
                          )}
                        </span>
                        <span>
                          {translate(
                            translations,
                            "blog_search_in",
                            language === "tr" ? "" : "in"
                          )}{" "}
                          {searchTime}ms
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
}

